import type { TFunction } from "i18next";

// Изтичащ лиценз (BE `app/features/user_sessions/license_notice.py`).
// КОГА се показва предупреждението решава само BE: успешният вход (парола на
// личен акаунт или служител с ПИН) носи `license_notice`, когато е минал
// интервалът за този човек (4/3/2/1 h по оставащото време). FE само го показва
// веднага след входа — модал с един бутон „Разбрах", никога на /monitors.
// След гратисния период трите входа връщат 403 с `detail.code ===
// LICENSE_EXPIRED_CODE` и FE показва същия модал в „спрян" вид. Същата 403
// идва и на ВСЯКА заявка на вече влезлите (а станционният WS праща кадър със
// същото тяло преди 1008) — тогава сесията свършва изцяло
// (`endSessionForExpiredLicense` в sessionRecovery.ts) и модалът е на /login.
// Изключение — „Справки"/„Склад": режим „само преглед" (`licenseReadOnly.ts`)
// със същия модал във вид „Само преглед".

/** `LoginOut.license_notice` (BE `LicenseNoticeOut`) — snake_case като целия LoginOut. */
export interface LicenseNotice {
  /** `licenses.license_to` — ISO с офсет. */
  expires_at: string;
  /** `license_to` + гратисния период (3 дни) — след него входът спира. */
  grace_ends_at: string;
  /** Календарни дни (Europe/Sofia) до `expires_at`; 0 в деня на изтичането и през гратиса. */
  days_left: number;
  /** true = лицензът е изтекъл, тече гратисният период. */
  expired: boolean;
  /** true = гратисът е минал, входът е в режим „само преглед" (BE `read_only`). */
  read_only?: boolean;
}

/** `detail.code` на 403-ката след гратисния период — огледало на BE `LICENSE_EXPIRED_CODE`. */
export const LICENSE_EXPIRED_CODE = "license_expired";

/** Тялото (`detail`) на 403-ката след гратисния период. */
export interface LicenseBlockedDetail {
  code: typeof LICENSE_EXPIRED_CODE;
  /** Готов български текст от BE — за клиенти, които не познават `code`. */
  message: string;
  expires_at: string;
  grace_ends_at: string;
}

/** Предупреждението, чакащо „Разбрах" (Redux `auth.licenseNotice`). */
export interface PendingLicenseNotice {
  notice: LicenseNotice;
  /** true = входът е отказан (гратисът е минал), false = предупреждение след успешен вход. */
  blocked: boolean;
}

export const isLicenseBlockedDetail = (
  value: unknown,
): value is LicenseBlockedDetail => {
  if (!value || typeof value !== "object") return false;
  const detail = value as Partial<Record<keyof LicenseBlockedDetail, unknown>>;
  return (
    detail.code === LICENSE_EXPIRED_CODE &&
    typeof detail.expires_at === "string" &&
    typeof detail.grace_ends_at === "string"
  );
};

/** Кадърът на станционния WebSocket преди затварянето с 1008:
 * `{type: "license_expired", data}` — `data` е `detail`-ът на 403-ката. */
export const licenseBlockedFromSocketFrame = (
  frame: unknown,
): LicenseBlockedDetail | null => {
  if (!frame || typeof frame !== "object") return null;
  const { type, data } = frame as { type?: unknown; data?: unknown };
  return type === LICENSE_EXPIRED_CODE && isLicenseBlockedDetail(data)
    ? data
    : null;
};

export const blockedLicenseNotice = (
  detail: LicenseBlockedDetail,
): PendingLicenseNotice => ({
  notice: {
    expires_at: detail.expires_at,
    grace_ends_at: detail.grace_ends_at,
    days_left: 0,
    expired: true,
  },
  blocked: true,
});

/** Модалът „Само преглед" — след вход в режима, при клик на друг модул или на
 * бутон за действие (`licenseReadOnly.ts`). `grace_ends_at` не се показва. */
export const readOnlyLicenseNotice = (expiresAt: string): PendingLicenseNotice => ({
  notice: {
    expires_at: expiresAt,
    grace_ends_at: expiresAt,
    days_left: 0,
    expired: true,
    read_only: true,
  },
  blocked: false,
});

/** Кухненските/барските екрани нямат такъв модал (решение на потребителя). */
export const isMonitorScreenPath = (pathname: string) =>
  pathname === "/monitors" || pathname.startsWith("/monitors/");

// Датите се показват в часа на заведението, не на устройството — лицензът
// изтича в полунощ Europe/Sofia (контролният панел пише датата така).
const VENUE_TIME_ZONE = "Europe/Sofia";

const venueParts = (iso: string, options: Intl.DateTimeFormatOptions) =>
  Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", { timeZone: VENUE_TIME_ZONE, ...options })
      .formatToParts(new Date(iso))
      .map((part) => [part.type, part.value]),
  );

/** „ДД.ММ.ГГГГ" в Europe/Sofia. */
export const formatVenueDate = (iso: string): string => {
  const parts = venueParts(iso, { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${parts.day}.${parts.month}.${parts.year}`;
};

/** „ЧЧ:ММ" (24 h) в Europe/Sofia. */
export const formatVenueTime = (iso: string): string => {
  const parts = venueParts(iso, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${parts.hour}:${parts.minute}`;
};

export type LicenseNoticeVariant = "warning" | "grace" | "blocked" | "readOnly";

export interface LicenseNoticeTexts {
  variant: LicenseNoticeVariant;
  /** „Достъпът ще спре след" (броячът е отделно в `countdown`) / „Достъпът е спрян" / „Само преглед". */
  title: string;
  /** Оставащото време („5 ч 55 мин") — null, когато достъпът е спрян. */
  countdown: string | null;
  /** „Лицензът изтича на ДД.ММ.ГГГГ в ЧЧ:ММ ч." (след изтичането — „изтече"). */
  subtitle: string;
  /** Призивът да се поднови лицензът. */
  message: string;
}

/** Оставащото време с кратки единици за продължителност — „5 ч 55 мин",
 * „6 ч", „42 мин", „168 ч"; никога в дни (изрично искане). Не „05:55":
 * след „след" се чете като час от деня. Започнатата минута се брои цяла, за
 * да не излиза „0 мин", докато достъпът още не е спрян. */
export function formatCountdown(milliseconds: number, t: TFunction): string {
  const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(t("licenseCountdown.hours", { value: hours }));
  if (minutes > 0) parts.push(t("licenseCountdown.minutes", { value: minutes }));
  return parts.join(" ");
}

/**
 * Текстовете на модала към момента `now` (модалът го опреснява, докато е
 * отворен). Фазата се смята по часовника, не само по `expired` от входа:
 * преди `expires_at` броим до изтичането, след него — до края на гратиса,
 * а след гратиса (или при отказан вход) достъпът е спрян. Режимът „само
 * преглед" (`read_only`) идва от облака; `readOnlyAfterGrace` (човекът има
 * „Справки"/„Склад") обръща в него и брояч, оставен отворен след края на гратиса.
 */
export function describeLicenseNotice(
  pending: PendingLicenseNotice,
  t: TFunction,
  now: Date,
  readOnlyAfterGrace = false,
): LicenseNoticeTexts {
  const { notice } = pending;
  const expiry = {
    date: formatVenueDate(notice.expires_at),
    time: formatVenueTime(notice.expires_at),
  };
  const nowMs = now.getTime();
  const expiresAtMs = Date.parse(notice.expires_at);
  const graceEndsAtMs = Date.parse(notice.grace_ends_at);

  // Преди „спрян" по часовника: в този режим гратисът по дефиниция е минал.
  if (
    !pending.blocked &&
    (notice.read_only || (readOnlyAfterGrace && nowMs >= graceEndsAtMs))
  ) {
    return {
      variant: "readOnly",
      title: t("licenseNotice.modal.titleReadOnly"),
      countdown: null,
      subtitle: t("licenseNotice.modal.expiredAt", expiry),
      message: t("licenseNotice.modal.readOnly"),
    };
  }
  if (pending.blocked || nowMs >= graceEndsAtMs) {
    return {
      variant: "blocked",
      title: t("licenseNotice.modal.titleBlocked"),
      countdown: null,
      subtitle: t("licenseNotice.modal.expiredAt", expiry),
      message: t("licenseNotice.modal.contactBlocked"),
    };
  }
  const inGrace = notice.expired || nowMs >= expiresAtMs;
  const stopsAtMs = inGrace ? graceEndsAtMs : expiresAtMs;
  return {
    variant: inGrace ? "grace" : "warning",
    title: t("licenseNotice.modal.titleCountdown"),
    countdown: formatCountdown(stopsAtMs - nowMs, t),
    subtitle: t(
      inGrace ? "licenseNotice.modal.expiredAt" : "licenseNotice.modal.expiresAt",
      expiry,
    ),
    message: t("licenseNotice.modal.contact"),
  };
}
